import {
  CompanyOrderBy,
  type CompanyOrderByType,
  type EntityCompanyListItemDto,
  OfferStatus,
  type OfferStatusType,
  OfferStructure,
  OrderDirection,
  type OrderDirectionType,
} from '@/api/deal/types';
import { formatDefaultFractionTwo, formatNumberWithLetter } from '@/utils/numbers/formatters';

import type { CompanyRow } from '../entity-deal/provider-dashboard/types';

export const mapCompanyToRow = (sourceCompany: EntityCompanyListItemDto): CompanyRow => ({
  id: sourceCompany.id,
  company: sourceCompany?.legalName || sourceCompany?.name,
  companyName: sourceCompany.name,
  logoImageId: sourceCompany.logoImageId ?? null,
  valuation:
    typeof sourceCompany.valuation === 'number'
      ? `$${formatNumberWithLetter(sourceCompany.valuation)}`
      : '—',
  sharePriceFrom:
    typeof sourceCompany.sharePrice === 'number'
      ? `$${formatDefaultFractionTwo(sourceCompany.sharePrice)}`
      : '—',
  minTicketSize:
    typeof sourceCompany.minTicketSize === 'number'
      ? `$${formatDefaultFractionTwo(sourceCompany.minTicketSize)}`
      : '—',
  totalAmountAvailable:
    typeof sourceCompany.totalAmountAvailable === 'number'
      ? `$${formatNumberWithLetter(sourceCompany.totalAmountAvailable)}`
      : '—',
  offersCount: sourceCompany.offersCount ?? 0,
  sector: sourceCompany.sectorName ?? '—',
});

export const DEFAULT_INQUIRE_MESSAGE =
  'I would like to learn more about this investment opportunity.';

export type CompanySortOptionId =
  | 'recently-updated'
  | 'valuation'
  | 'share-price'
  | 'min-investment'
  | 'available-size'
  | 'open-offers'
  | 'company-name';

export type CompanySortOption = {
  id: CompanySortOptionId;
  label: string;
  menuLabel: string;
  orderBy: CompanyOrderByType;
  direction: OrderDirectionType;
};

export const COMPANY_SORT_OPTIONS: readonly CompanySortOption[] = [
  {
    id: 'recently-updated',
    label: 'Recently updated',
    menuLabel: 'Recently updated',
    orderBy: CompanyOrderBy.RecentlyUpdated,
    direction: OrderDirection.Desc,
  },
  {
    id: 'valuation',
    label: 'Valuation',
    menuLabel: 'Valuation · high to low',
    orderBy: CompanyOrderBy.Valuation,
    direction: OrderDirection.Desc,
  },
  {
    id: 'share-price',
    label: 'Share price',
    menuLabel: 'Share price · low to high',
    orderBy: CompanyOrderBy.SharePrice,
    direction: OrderDirection.Asc,
  },
  {
    id: 'min-investment',
    label: 'Min. investment',
    menuLabel: 'Min. investment · low to high',
    orderBy: CompanyOrderBy.MinInvestment,
    direction: OrderDirection.Asc,
  },
  {
    id: 'available-size',
    label: 'Available size',
    menuLabel: 'Available size · high to low',
    orderBy: CompanyOrderBy.AvailableSize,
    direction: OrderDirection.Desc,
  },
  {
    id: 'open-offers',
    label: 'Open offers · most first',
    menuLabel: 'Open offers · most first',
    orderBy: CompanyOrderBy.OffersCount,
    direction: OrderDirection.Desc,
  },
  {
    id: 'company-name',
    label: 'Company name',
    menuLabel: 'Company name · A to Z',
    orderBy: CompanyOrderBy.Name,
    direction: OrderDirection.Asc,
  },
];

export const DEFAULT_COMPANY_SORT_ID: CompanySortOptionId = 'open-offers';

export const getCompanySortOption = (id: CompanySortOptionId) =>
  COMPANY_SORT_OPTIONS.find((option) => option.id === id) ?? COMPANY_SORT_OPTIONS[0];

type OfferStatusUi = {
  label: string;
  color: string;
  background: string;
};

const CLOSED_STATUS_UI = {
  color: 'rgba(255, 255, 255, 0.70)',
  background: 'rgba(255, 255, 255, 0.10)',
} as const;

export const getOfferStatusUi = (status?: OfferStatusType): OfferStatusUi => {
  switch (status) {
    case OfferStatus.Open:
      return {
        label: 'Active',
        color: '#41B285',
        background: 'rgba(57, 202, 136, 0.25)',
      };
    case OfferStatus.Pending:
      return {
        label: 'Draft',
        color: '#AED3FF',
        background: 'rgba(174, 211, 255, 0.10)',
      };
    case OfferStatus.Cancelled:
      return { label: 'Cancelled', ...CLOSED_STATUS_UI };
    case OfferStatus.Suspended:
      return { label: 'Suspended', ...CLOSED_STATUS_UI };
    default:
      return { label: 'Closed', ...CLOSED_STATUS_UI };
  }
};

export const formatOfferStructure = (structure: string) => {
  switch (structure) {
    case OfferStructure.Direct:
      return 'Direct';
    case OfferStructure.Layer1:
      return 'Layer 1';
    case OfferStructure.Layer2:
      return 'Layer 2';
    default:
      return structure;
  }
};
